/**
 * Users on a phone (SAM-944, mocks M1 and M3): the list as cards, the tabs as chips, and on the requests tab an
 * Approve / Reject pair on each card. Presentational: Users.tsx owns the data, the search and the mutations.
 */
import React from 'react';
import { ChevronRight, Search } from 'lucide-react';
import {
  Initials, PhoneChips, PhoneListState, PhonePager, PhonePill, cardClass, type PhoneChip, type PillTone,
} from '../../components/phone/PhoneCards';

export interface PhoneUserItem {
  key: string;
  userId: number;
  name: string;
  initials: string;
  sub: string;
  statusLabel: string;
  statusTone: PillTone;
}

export interface PhoneRequestItem {
  key: string;
  userId: number;
  estateId: number;
  name: string;
  initials: string;
  wants: string;
  sent: string;
  old: boolean;
}

interface Props {
  title: string;
  summary: string;
  searchValue: string;
  onSearch: (value: string) => void;
  chips: PhoneChip[];
  activeChip: string;
  onChip: (id: string) => void;
  mode: 'users' | 'requests';
  users: PhoneUserItem[];
  requests: PhoneRequestItem[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
  onOpen: (userId: number) => void;
  /** Approve / Reject are offered only when this is given (the person may decide, and the statuses exist). */
  onDecide?: (item: PhoneRequestItem, decision: 'approve' | 'reject') => void;
  canReject: boolean;
  /** A request being decided right now: its buttons wait. */
  busyKey?: string | null;
  emptyText: string;
}

export const UsersPhoneView: React.FC<Props> = (p) => (
  <div className="flex h-full min-w-0 flex-col gap-3 overflow-y-auto bg-[var(--surface)] px-4 pb-8 pt-4">
    <div>
      <h1 className="m-0 text-2xl font-bold">{p.title}</h1>
      <div className="mt-0.5 text-[13px] text-[var(--ink-500)]">{p.summary}</div>
    </div>

    <label className="flex h-11 items-center gap-2 rounded-lg border border-solid border-[var(--line)] bg-white px-3 text-[var(--ink-500)]">
      <Search size={16} aria-hidden />
      <input
        aria-label="Search by name, email, phone or estate"
        placeholder="Search by name, email, phone or estate"
        value={p.searchValue}
        onChange={(e) => p.onSearch(e.target.value)}
        className="min-w-0 grow border-0 bg-transparent text-[15px] outline-none"
      />
    </label>

    <PhoneChips chips={p.chips} active={p.activeChip} onPick={p.onChip} />

    <PhoneListState
      loading={p.loading}
      failed={p.failed}
      empty={!p.loading && !p.failed && (p.mode === 'users' ? p.users.length === 0 : p.requests.length === 0)}
      emptyText={p.emptyText}
      onRetry={p.onRetry}
    />

    {!p.loading && !p.failed && p.mode === 'users' && (
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {p.users.map((u) => (
          <li key={u.key}>
            <button
              type="button"
              onClick={() => p.onOpen(u.userId)}
              className={`${cardClass} flex w-full items-center gap-3 border-0 px-4 py-3.5 text-left text-[var(--ink-900)]`}
            >
              <Initials text={u.initials} />
              <span className="flex min-w-0 grow flex-col gap-0.5">
                <span className="text-[15px] font-semibold">{u.name}</span>
                <span className="truncate text-[13px] text-[var(--ink-500)]">{u.sub}</span>
              </span>
              <PhonePill tone={u.statusTone}>{u.statusLabel}</PhonePill>
            </button>
          </li>
        ))}
      </ul>
    )}

    {!p.loading && !p.failed && p.mode === 'requests' && (
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {p.requests.map((r) => {
          const busy = p.busyKey === r.key;
          return (
            <li key={r.key} className={`${cardClass} flex flex-col gap-2.5 px-4 py-3.5`}>
              <button
                type="button"
                onClick={() => p.onOpen(r.userId)}
                className="flex w-full items-center gap-3 border-0 bg-transparent p-0 text-left text-[var(--ink-900)]"
              >
                <Initials text={r.initials} warn />
                <span className="flex min-w-0 grow flex-col gap-0.5">
                  <span className="text-[15px] font-semibold">{r.name}</span>
                  <span className="text-[13px] text-[var(--ink-500)]">{r.wants}</span>
                  <span className={`text-xs font-semibold ${r.old ? 'text-[#B45309]' : 'text-[var(--ink-500)]'}`}>{r.sent}</span>
                </span>
                <ChevronRight size={18} className="shrink-0 text-[var(--ink-400)]" aria-hidden />
              </button>
              {p.onDecide && (
                <div className={`grid gap-2 ${p.canReject ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {p.canReject && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => p.onDecide?.(r, 'reject')}
                      aria-label={`Reject ${r.name}`}
                      className="h-11 rounded-lg border border-solid border-[var(--line)] bg-white text-sm font-semibold text-[#B91C1C] disabled:opacity-50"
                    >
                      Reject
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => p.onDecide?.(r, 'approve')}
                    aria-label={`Approve ${r.name}`}
                    className="h-11 rounded-lg border-0 bg-[var(--tenant-primary)] text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Approve
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    )}

    {!p.loading && !p.failed && <PhonePager page={p.page} limit={p.limit} total={p.total} onPage={p.onPage} />}
  </div>
);
